import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/links")({
  head: () => ({
    meta: [
      ...routeMeta("/links"),
      { title: "Tax & Accounting Resources | CCOB & Company PA" },
      { name: "description", content: "A curated directory of tax, accounting, financial, government, and educational links — selected by CCOB And Company PA." },
      { property: "og:title", content: "Useful Links — CCOB And Company PA" },
      { property: "og:description", content: "Curated tax, accounting, and government resources." },
    ],
    links: canonicalLinks("/links"),
  }),
  component: LinksPage,
});

type Link = { label: string; url: string };
type Category = { name: string; description: string; links: Link[] };

const categories: Category[] = [
  {
    name: "Accounting.com",
    description: "Educational resources for accounting students and professionals.",
    links: [
      { label: "Guide to a Master's Degree in Accounting", url: "https://www.accounting.com/degrees/masters/" },
      { label: "Guide to Accounting in Florida", url: "https://www.accounting.com/states/florida/" },
      { label: "Guide to Financial Aid in Accounting", url: "https://www.accounting.com/financial-aid/" },
      { label: "Guide to Accounting Value for Small Businesses", url: "https://www.accounting.com/resources/accounting-basics-small-business/" },
    ],
  },
  {
    name: "New Employee Forms",
    description: "Standard federal forms every new hire needs to complete.",
    links: [
      { label: "Form W-4 (Employee's Withholding Certificate)", url: "https://www.irs.gov/forms-pubs/about-form-w-4" },
      { label: "Form I-9 (Employment Eligibility Verification)", url: "https://www.uscis.gov/i-9" },
      { label: "Florida New Hire Reporting Center", url: "https://newhire-reporting.com/FL-Newhire/default.aspx" },
    ],
  },
  {
    name: "Financial Links",
    description: "Markets, rates, and personal finance reference sites.",
    links: [
      { label: "Bloomberg", url: "https://www.bloomberg.com/" },
      { label: "Yahoo Finance", url: "https://finance.yahoo.com/" },
      { label: "Federal Reserve", url: "https://www.federalreserve.gov/" },
      { label: "Bankrate", url: "https://www.bankrate.com/" },
    ],
  },
  {
    name: "Government Links",
    description: "Federal and state agency resources for taxpayers and businesses.",
    links: [
      { label: "Internal Revenue Service (IRS)", url: "https://www.irs.gov/" },
      { label: "Florida Department of Revenue", url: "https://floridarevenue.com/" },
      { label: "Social Security Administration", url: "https://www.ssa.gov/" },
      { label: "U.S. Small Business Administration", url: "https://www.sba.gov/" },
      { label: "SunBiz — Florida Division of Corporations", url: "https://dos.fl.gov/sunbiz/" },
    ],
  },
  {
    name: "Consumer Advice",
    description: "Trusted consumer protection and financial-literacy resources.",
    links: [
      { label: "Consumer Financial Protection Bureau", url: "https://www.consumerfinance.gov/" },
      { label: "Federal Trade Commission — Consumer Info", url: "https://consumer.ftc.gov/" },
      { label: "Better Business Bureau", url: "https://www.bbb.org/" },
      { label: "AnnualCreditReport.com", url: "https://www.annualcreditreport.com/" },
    ],
  },
  {
    name: "Internet Resources",
    description: "General-purpose tools we recommend.",
    links: [
      { label: "USPS — ZIP Code Lookup", url: "https://tools.usps.com/zip-code-lookup.htm" },
      { label: "Google", url: "https://www.google.com/" },
      { label: "Currency Converter (XE)", url: "https://www.xe.com/" },
    ],
  },
  {
    name: "News And Magazines",
    description: "Business and financial publications worth following.",
    links: [
      { label: "The Wall Street Journal", url: "https://www.wsj.com/" },
      { label: "Forbes", url: "https://www.forbes.com/" },
      { label: "CNBC", url: "https://www.cnbc.com/" },
      { label: "Journal of Accountancy", url: "https://www.journalofaccountancy.com/" },
    ],
  },
  {
    name: "Software Links",
    description: "Accounting and productivity software we work with daily.",
    links: [
      { label: "QuickBooks Online", url: "https://quickbooks.intuit.com/online/" },
      { label: "Microsoft 365", url: "https://www.microsoft.com/en-us/microsoft-365" },
      { label: "Adobe Acrobat Reader", url: "https://get.adobe.com/reader/" },
      { label: "Zoom", url: "https://zoom.us/" },
    ],
  },
  {
    name: "Education Links",
    description: "Florida higher-education and CPA-pipeline resources.",
    links: [
      { label: "Florida Department of Education", url: "https://www.fldoe.org/" },
      { label: "AICPA — Become a CPA", url: "https://www.aicpa-cima.com/become-a-cpa" },
      { label: "Florida Board of Accountancy", url: "https://www2.myfloridalicense.com/dbpr/cpa/" },
    ],
  },
  {
    name: "Just For Fun",
    description: "A few favorites for when you need a break.",
    links: [
      { label: "Visit Florida", url: "https://www.visitflorida.com/" },
      { label: "Walt Disney World", url: "https://disneyworld.disney.go.com/" },
      { label: "Winter Garden, FL — Official Site", url: "https://www.cwgdn.com/" },
    ],
  },
];

function LinksPage() {
  const [active, setActive] = useState(0);
  const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };
  const cat = categories[active];
  return (
    <>
      <PageHero
        eyebrow="Useful Links"
        title="Curated Tax, Accounting & Financial Resources"
        subtitle="A directory of trusted external resources — government agencies, financial tools, software, and more — selected by CCOB And Company PA."
        image={img.heroResources}
      />
      <section className="py-20">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-4">Categories</div>
            <div className="flex flex-col gap-1.5">
              {categories.map((c, i) => (
                <button
                  key={c.name}
                  onClick={() => setActive(i)}
                  className={`text-left px-4 py-3 rounded-lg border transition text-sm font-medium ${
                    i === active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card border-border text-foreground hover:border-gold"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </aside>

          {/* Active category */}
          <motion.div key={cat.name} {...fadeUp} className="lg:col-span-8">
            <h2 className="font-display text-3xl md:text-4xl text-primary">{cat.name}</h2>
            <div className="gold-line mt-4" />
            <p className="mt-4 text-muted-foreground">{cat.description}</p>
            <ul className="mt-8 divide-y divide-border border border-border rounded-2xl bg-card overflow-hidden">
              {cat.links.map((l) => (
                <li key={l.url}>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between gap-4 p-5 hover:bg-secondary/40 transition group"
                  >
                    <span className="font-medium text-primary group-hover:text-gold transition">{l.label}</span>
                    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-gold transition shrink-0" />
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-muted-foreground">
              External links are provided for informational purposes only. CCOB And Company PA does not endorse or control the content of third-party websites.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}
